/**
 * ops SLO ボリューム推移チャート。直近 6 / 12 ヶ月の月別「件数（起票数）」「工数（point 合計）」を棒で、
 * 「SLO 達成率」を右軸の折れ線で重ね、目標ライン（OPS_SLO_TARGET_PERCENT）を破線で示す。月バケットと
 * 起票日の JST 振り分けは ops-core（`opsSloTrendMonths` / `jstYearMonthKey`）、集計は共有の
 * ops-core の `slo-aggregate` を単一 source に使い、サマリーカードと指標定義を一致させる。
 * @graph-stack product-innovation-lab
 * @graph-domain Operations
 * @graph-business ops SLO ボリューム推移チャート。直近 6 / 12 ヶ月の月別 件数・工数(point)を棒、SLO 達成率を右軸折れ線で表示し、目標ラインを重ねる。月バケットと集計は ops-core / 共有集計に委譲
 * @graph-connects buildOpsSloTrendSeries [calls] 依頼配列を月別の件数 / 工数 / SLO 達成率へ集計
 */
import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ComposedChart,
  Line,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  ResponsiveContainer,
} from 'recharts';
import {
  OPS_SLO_TARGET_PERCENT,
  jstYearMonthKey,
  opsSloRate,
  opsSloTrendMonths,
  opsTotalCount,
  opsTotalPoint,
  type OpsSloTrendMonths,
} from '@cortex/ops-core';
import { Card } from '@cortex/web-design-system/components/ui/Card';
import { TabButton } from '../../ui/Tabs/TabButton.js';
import { TabGroup } from '../../ui/Tabs/TabGroup.js';
import type { OpsRequest } from '../../../types/ops.js';
import { OpsAggregatingPanel, OpsFetchErrorPanel } from './OpsDashboardStates.js';

/** 件数の棒の色（インディゴ）。 @graph-connects none */
const COUNT_COLOR = 'var(--color-accent-primary)';
/** 工数(point)の棒の色（情報色）。 @graph-connects none */
const POINT_COLOR = 'var(--color-info)';
/** SLO 達成率の折れ線の色（ピンク）。 @graph-connects none */
const SLO_COLOR = '#ec4899';
/** 目標ライン（破線）の色。 @graph-connects none */
const TARGET_COLOR = 'var(--color-text-muted)';

/** ツールチップ共通スタイル（DevelopPointsTrendCard と同じ）。 @graph-connects none */
const TOOLTIP_CONTENT_STYLE = {
  backgroundColor: 'var(--color-bg-elevated)',
  border: '1px solid var(--color-border)',
  borderRadius: '8px',
  boxShadow: 'var(--shadow-lg)',
};

/** SLO ボリューム推移の 1 月分の集計値。 @graph-connects none */
export interface OpsSloTrendPoint {
  /** 月バケットキー（YYYY-MM）。 */
  key: string;
  /** 表示ラベル（"Aug 2026"）。 */
  label: string;
  /** その月に起票された件数。 */
  count: number;
  /** その月の工数（point）合計。 */
  point: number;
  /** その月の SLO 達成率（%）。SLA 対象が無ければ null（線を繋がない）。 */
  slo: number | null;
}

/**
 * 依頼配列を直近 N ヶ月の月別系列（件数 / 工数 / SLO 達成率）へ集計する。月バケットは ops-core の
 * `opsSloTrendMonths`（古い順）、起票日の振り分けは `jstYearMonthKey`（JST）で行い、件数 / 工数 / SLO は
 * 共有集計（ops-core `slo-aggregate`）に委譲する。窓外の月に落ちた依頼は無視する。
 * @graph-stack product-innovation-lab
 * @graph-domain Operations
 * @graph-business ops 依頼配列を直近 N ヶ月の月別（件数 / 工数 / SLO 達成率）へ集計する純関数（推移チャートの元系列）
 * @graph-connects opsSloTrendMonths [calls] 月バケット列（古い順）を取得
 * @graph-connects jstYearMonthKey [calls] 起票日を JST の月バケットキーへ写す
 * @graph-connects opsSloRate [calls] 月内の SLO 達成率を算出
 * @graph-connects opsTotalCount [calls] 月内の件数を算出
 * @graph-connects opsTotalPoint [calls] 月内の工数合計を算出
 */
export function buildOpsSloTrendSeries(
  rows: OpsRequest[],
  months: OpsSloTrendMonths,
  now?: Date,
): OpsSloTrendPoint[] {
  const buckets = opsSloTrendMonths(months, now);
  const byKey = new Map<string, OpsRequest[]>(buckets.map((b) => [b.key, []]));
  for (const r of rows) {
    const key = jstYearMonthKey(r.submittedAt);
    const bucket = key === null ? undefined : byKey.get(key);
    if (bucket !== undefined) bucket.push(r);
  }
  return buckets.map((b) => {
    const monthRows = byKey.get(b.key) ?? [];
    return {
      key: b.key,
      label: b.label,
      count: opsTotalCount(monthRows),
      point: opsTotalPoint(monthRows),
      slo: opsSloRate(monthRows),
    };
  });
}

/** OpsSloTrendChart の props。 */
interface OpsSloTrendChartProps {
  /** 推移窓（直近 N ヶ月）の依頼（サービス絞り込み適用済み）。 */
  rows: OpsRequest[];
  /** 遡り月数（6 / 12）。 */
  months: OpsSloTrendMonths;
  /** 遡り月数の変更ハンドラ。 */
  onMonthsChange: (months: OpsSloTrendMonths) => void;
  /** 推移データ取得中か。 */
  isLoading: boolean;
  /** 取得失敗したか。 */
  isError: boolean;
  /** 再取得ハンドラ。 */
  onRetry: () => void;
}

/** 凡例の 1 項目（色スウォッチ + ラベル）。 @graph-connects none */
function LegendItem({ swatch, label }: { swatch: ReactNode; label: string }): ReactNode {
  return (
    <span className="flex items-center gap-1">
      {swatch}
      {label}
    </span>
  );
}

/** SLO ボリューム推移チャート。 @graph-connects none */
export function OpsSloTrendChart({
  rows,
  months,
  onMonthsChange,
  isLoading,
  isError,
  onRetry,
}: OpsSloTrendChartProps): ReactNode {
  const { t } = useTranslation();
  // 月バケットは client の JST 現在月を基準に組む。fetch 窓は server の JST 現在月基準だが、両者とも同じ
  // getJstYmd 基準・リクエストはほぼ即時なので、JST 月替わりの一瞬を除き一致する（ずれても最新側の空月が
  // 1 つ増減する程度で同日中に解消するため、レスポンス契約に窓を載せる過剰実装はしない）。
  const series = buildOpsSloTrendSeries(rows, months);
  const hasVolume = series.some((p) => p.count > 0 || p.point > 0);

  const header = (
    <div className="flex flex-col gap-2 border-b border-[var(--color-border)] px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
      <h2 className="text-sm font-semibold text-[var(--color-text-primary)]">
        {t('ops.dashboard.trend.title')}
      </h2>
      <TabGroup ariaLabel={t('ops.dashboard.trend.monthsSwitchLabel')}>
        <TabButton
          active={months === 6}
          label={t('ops.dashboard.trend.months6')}
          onClick={() => {
            onMonthsChange(6);
          }}
        />
        <TabButton
          active={months === 12}
          label={t('ops.dashboard.trend.months12')}
          onClick={() => {
            onMonthsChange(12);
          }}
        />
      </TabGroup>
    </div>
  );

  return (
    <Card padding="none">
      {header}
      {isError ? (
        <OpsFetchErrorPanel onRetry={onRetry} />
      ) : isLoading ? (
        <OpsAggregatingPanel />
      ) : hasVolume ? (
        <div className="p-4">
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={series} margin={{ top: 5, right: 12, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis dataKey="label" stroke="var(--color-text-muted)" fontSize={11} />
                <YAxis
                  yAxisId="left"
                  stroke="var(--color-text-muted)"
                  fontSize={11}
                  allowDecimals={false}
                />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  domain={[0, 100]}
                  stroke="var(--color-text-muted)"
                  fontSize={11}
                  tickFormatter={(v: number) => `${String(v)}%`}
                />
                <Tooltip
                  wrapperStyle={{ zIndex: 50, outline: 'none' }}
                  contentStyle={TOOLTIP_CONTENT_STYLE}
                  labelStyle={{ color: 'var(--color-text-primary)' }}
                  formatter={
                    ((value: number, _name: string, item: { dataKey?: string }) => {
                      if (item.dataKey === 'slo') {
                        return [`${String(value)}%`, t('ops.dashboard.trend.legend.slo')];
                      }
                      if (item.dataKey === 'point') {
                        return [`${String(value)}pt`, t('ops.dashboard.trend.legend.points')];
                      }
                      return [String(value), t('ops.dashboard.trend.legend.count')];
                    }) as never
                  }
                />
                <ReferenceLine
                  yAxisId="right"
                  y={OPS_SLO_TARGET_PERCENT}
                  stroke={TARGET_COLOR}
                  strokeDasharray="4 4"
                />
                <Bar yAxisId="left" dataKey="count" fill={COUNT_COLOR} radius={[2, 2, 0, 0]} />
                <Bar yAxisId="left" dataKey="point" fill={POINT_COLOR} radius={[2, 2, 0, 0]} />
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="slo"
                  stroke={SLO_COLOR}
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 4, fill: SLO_COLOR }}
                  connectNulls
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-[var(--color-text-muted)]">
            <LegendItem
              swatch={
                <span className="inline-block h-2.5 w-2.5 rounded-sm bg-[var(--color-accent-primary)]" />
              }
              label={t('ops.dashboard.trend.legend.count')}
            />
            <LegendItem
              swatch={
                <span className="inline-block h-2.5 w-2.5 rounded-sm bg-[var(--color-info)]" />
              }
              label={t('ops.dashboard.trend.legend.points')}
            />
            <LegendItem
              swatch={<span className="inline-block w-4 border-t-2 border-[#ec4899]" />}
              label={t('ops.dashboard.trend.legend.slo')}
            />
            <LegendItem
              swatch={
                <span className="inline-block w-4 border-t-2 border-dashed border-[var(--color-text-muted)]" />
              }
              label={t('ops.dashboard.trend.legend.target', { target: OPS_SLO_TARGET_PERCENT })}
            />
          </div>
        </div>
      ) : (
        <div className="p-6 text-center text-sm text-[var(--color-text-muted)]">
          {t('ops.dashboard.trend.empty')}
        </div>
      )}
    </Card>
  );
}
